import type {
	ImportFileMappingFormData,
	ImportFromFileContextValue,
	ImportFromFileFormData,
} from '@/shared/components/ImportFromFileModal'
import type {
	OptimizationGroupModel,
	OptimizationModel,
} from '@/shared/generated'

import React, { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { capitalize, prop, uniq } from 'lodash/fp'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useColumnsSchemaQuery } from '@/entities/column'
import {
	resetOptimizations,
	useBulkOptimizationMutation,
	useImportOptimizationGroups,
} from '@/entities/optimization'

import { client } from '@/shared/api/client'
import {
	ImportFromFileErrors,
	ImportFromFileForm,
	ImportFromFileFormDescription,
	ImportFromFileFormFields,
	ImportFromFileMapping,
	ImportFromFileProcessing,
	ImportFromFileProvider,
	useImportFromFile,
} from '@/shared/components/ImportFromFileModal'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { clarify } from '@/shared/components/ui/clarify'
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import {
	HiddenOnContext,
	OptimizationAction,
	OptimizationStatus,
} from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { downloadProductsImportTemplate } from '@/shared/lib/download-xlsx'

import { getBukImportErrorSummary } from '../lib/getBukImportErrorSummary'
import { getBulkImportResult } from '../lib/getBulkImportResult'

type Props = {
	items: {
		group: OptimizationGroupModel
		optimization: OptimizationModel
	}[]
	onResetSelection(): void
	close(): void
	isOpen: boolean
	optimizationGroupsCount: number
}

export function ImportOptimizationGroupsModal({
	close,
	isOpen,
	items,
	optimizationGroupsCount,
	onResetSelection,
}: Props) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const importOptimizationGroupsMutation = useImportOptimizationGroups({
		async onSuccess() {
			queryClient.invalidateQueries({
				queryKey: ['pricing-alerts'],
			})

			return queryClient.invalidateQueries({
				queryKey: ['optimizations'],
			})
		},
	})
	const bulkActionMutation = useBulkOptimizationMutation()
	const columnsSchemaQuery = useColumnsSchemaQuery<
		{
			label: string
			value: string
		}[]
	>(HiddenOnContext.Assortment, {
		select: (data) =>
			data
				?.filter((column) => column.params.editable || column.id === 'sku')
				?.map((column) => ({
					label: t(column.name, { ns: 'fields', defaultValue: column.name }),
					value: column.id,
				})) || [],
	})
	const onSubmit = async (
		formData: ImportFromFileFormData & ImportFileMappingFormData,
		context: ImportFromFileContextValue
	) => {
		try {
			const lockedOptimizations = items.filter(
				({ optimization }) =>
					optimization?.is_exported &&
					optimization?.status === OptimizationStatus.Locked
			)
			const hasLockedOptimizations = lockedOptimizations?.length > 0

			if (hasLockedOptimizations) {
				const answer = await clarify({
					title: t('opt.confirm.revert_applied.title'),
					description: t('opt.confirm.revert_applied.description'),
					actions: [
						{
							actionId: 'keep_optimized_prices',
							ActionProps: { variant: 'secondary' },
							actionText: t(
								'opt.confirm.revert_applied.action.keep_optimized_prices'
							),
						},
						{
							actionId: 'revert_full_state',
							ActionProps: { variant: 'primary' },
							actionText: t(
								'opt.confirm.revert_applied.action.revert_full_state'
							),
						},
					],
				})
				if (!answer || answer === 'close') {
					return
				}

				if (answer === 'revert_full_state') {
					await bulkActionMutation.mutateAsync(
						lockedOptimizations.map(({ group }) => ({
							optimization_group_id: group.id,
							action: OptimizationAction.DeleteExport,
						}))
					)
				}
				context.setStep('processing')
			} else {
				context.setStep('processing')
			}
			const selected = items.map(prop('group.id'))

			ampli.massRepricingUpdateDataFromFile({
				'Ignore empty fields': formData.ignore_empties,
			})
			const optimizations = queryClient.getQueryData<OptimizationModel[]>([
				'optimizations',
			])
			await resetOptimizations(optimizations || [], selected || [])

			const responses = await importOptimizationGroupsMutation.mutateAsync({
				product_diffs_file: formData.product_diffs[0],
				ignore_empties: formData.ignore_empties,
				optimization_group_ids: JSON.stringify(selected),
				file_header_mapping: JSON.stringify(formData.file_header_mapping),
			})
			const { sku_total, og_total } = getBulkImportResult(responses)
			toast.info(
				`${t('og_bulk_import_will_be_processed_items')} ${sku_total} ${t('og_bulk_import_will_be_processed_og')} ${og_total}`
			)

			onResetSelection()
			close()
		} catch (e) {
			context.setStep('errors')
		}
	}
	const errors = useMemo(() => {
		const response = importOptimizationGroupsMutation.error?.response as any
		const error = response?.data
		if (importOptimizationGroupsMutation.isError) {
			if (error.errors) {
				return getBukImportErrorSummary(error.errors)
			} else {
				return t('fatal_error_title')
			}
		}

		return null
	}, [importOptimizationGroupsMutation, t])

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ImportFromFileProvider
				onSubmit={onSubmit}
				schemaOptions={columnsSchemaQuery.data || []}
			>
				<ModalContent>
					<ModalHeader>
						<ModalTitle>{t('og_bulk_import_title')}</ModalTitle>
					</ModalHeader>
					<ImportFromFileModalContent
						items={items}
						optimizationGroupsCount={optimizationGroupsCount}
						errors={errors}
					/>
				</ModalContent>
			</ImportFromFileProvider>
		</Modal>
	)
}

const ImportFromFileModalContent = ({
	items,
	optimizationGroupsCount,
	errors,
}: {
	errors: string | string[] | null | any
	optimizationGroupsCount: number
	items: {
		group: OptimizationGroupModel
		optimization: OptimizationModel
	}[]
}) => {
	const { t } = useTranslation()

	const { step } = useImportFromFile()
	const handleDownloadTemplate = async () => {
		const columns = await client.fieldSchema.fieldSchemaList({
			context: [HiddenOnContext.Assortment],
		})
		downloadProductsImportTemplate(
			uniq([
				'SKU',
				...columns
					.filter((column) => column.params.editable)
					.map((column) =>
						t(column.name, { ns: 'fields', defaultValue: column.name })
					),
			])
		)
	}

	return (
		<>
			{step === 'form' && (
				<ImportFromFileForm>
					<div className='flex items-center gap-1 text-sm'>
						<strong>{items.length}</strong>
						<span>{t('general_of')}</span>
						<strong>{optimizationGroupsCount}</strong>
						<span>{t('og_bulk_import_count_hint')}</span>
					</div>
					<ImportFromFileFormFields />
					<ImportFromFileFormDescription
						onDownloadTemplate={handleDownloadTemplate}
					/>
					<Alert variant='warning'>
						<InfoIcon />
						<AlertTitle>
							{t('general_note', { defaultValue: 'Note' })}
						</AlertTitle>
						<AlertDescription>
							{t('og_import_optimizations_hint')}
						</AlertDescription>
					</Alert>
				</ImportFromFileForm>
			)}
			{step === 'mapping' && <ImportFromFileMapping />}
			{step === 'processing' && <ImportFromFileProcessing />}
			{step === 'errors' && (
				<ImportFromFileErrors>
					<Alert variant='destructive'>
						<AlertTitle>{t('general_error_types')}:</AlertTitle>
						<AlertDescription>
							{errors && Array.isArray(errors) ? (
								<div className='text-sm'>
									{errors.map((error, i) => (
										<p key={error + ' ' + i}>
											{capitalize(error.error.split('_').join(' '))}
											<span className='ml-1'>in</span>
											<span className='mx-1 text-primary'>{error.field}</span>
											column:
											<span className='mx-1 text-primary'>{error.rows}</span>
										</p>
									))}
								</div>
							) : (
								<div className='grid gap-2 text-sm'>
									<p>{errors}</p>
									<p>{t('general_possible_reasons')}:</p>
									<p
										className='ml-4'
										// eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
										dangerouslySetInnerHTML={{
											__html: t('og_import_error_hint_html'),
										}}
									/>
								</div>
							)}
						</AlertDescription>
					</Alert>
				</ImportFromFileErrors>
			)}
		</>
	)
}
